import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { usePrintSettings } from "@/contexts/PrintSettingsContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import UncollectedBillingTile from "@/components/dashboard/UncollectedBillingTile";
import { 
  Package, 
  Truck, 
  Calculator, 
  StickyNote,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Globe,
  Building2,
  FolderOpen,
  Tag,
  Users,
  DollarSign,
  ArrowRight,
  RefreshCw,
  FileText,
  X,
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Printer,
  Eye,
  Wallet,
  CreditCard,
  Clock,
  BarChart3,
  Upload,
  FileSpreadsheet,
  CircleDollarSign,
  PieChart,
  Activity,
  Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { DataIntegrityWidget } from "@/components/DataIntegrityWidget";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { formatDMY } from "@/lib/dmy";

const DashboardPage = () => {
  usePageTitle("Dashboard");
  const { token } = useAuth();
  const { settings: printSettings } = usePrintSettings();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showClientReport, setShowClientReport] = useState(false);
  const [clientReport, setClientReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'total', direction: 'desc' });
  
  // Date range filters for each section
  const [ledgerDateRange, setLedgerDateRange] = useState({ from: null, to: null });
  const [costingDateRange, setCostingDateRange] = useState({ from: null, to: null });
  const [salesDateRange, setSalesDateRange] = useState({ from: null, to: null });
  const [errorsDateRange, setErrorsDateRange] = useState({ from: null, to: null });
  const [uploadsDateRange, setUploadsDateRange] = useState({ from: null, to: null });
  
  // Orders list state
  const [showOrdersList, setShowOrdersList] = useState(false);
  const [ordersList, setOrdersList] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersSortConfig, setOrdersSortConfig] = useState({ key: 'total', direction: 'desc' });
  
  // Client detail modal state
  const [showClientDetail, setShowClientDetail] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientOrders, setClientOrders] = useState([]);
  const [loadingClientOrders, setLoadingClientOrders] = useState(false);
  const [clientDetailPage, setClientDetailPage] = useState(1);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printColumns, setPrintColumns] = useState({
    order_date: true,
    tracking: true,
    product: true,
    qty: true,
    service: true,
    country: true,
    med_cost: true,
    ship_cost: true,
    total: true
  });

  // Helper function to open report in new tab with current date filter
  const openReportInNewTab = (reportPath, dateRange) => {
    const params = new URLSearchParams();
    if (dateRange?.from) {
      params.append('from', format(dateRange.from, 'yyyy-MM-dd'));
    }
    if (dateRange?.to) {
      params.append('to', format(dateRange.to, 'yyyy-MM-dd'));
    }
    const queryString = params.toString();
    const url = `${reportPath}${queryString ? `?${queryString}` : ''}`;
    window.open(url, '_blank');
  };

  // Sorted client data
  const sortedClients = useMemo(() => {
    if (!clientReport?.clients) return [];
    const sorted = [...clientReport.clients];
    sorted.sort((a, b) => {
      const aVal = a[sortConfig.key] || 0;
      const bVal = b[sortConfig.key] || 0;
      if (sortConfig.key === 'client') {
        return sortConfig.direction === 'asc' 
          ? (aVal || '').localeCompare(bVal || '')
          : (bVal || '').localeCompare(aVal || '');
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [clientReport?.clients, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };
  
  const handleOrdersSort = (key) => {
    setOrdersSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
    // Refetch with new sort
    fetchOrdersList(1, key, ordersSortConfig.key === key && ordersSortConfig.direction === 'desc' ? 'asc' : 'desc');
  };

  const SortIcon = ({ columnKey, config = sortConfig }) => {
    if (config.key !== columnKey) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return config.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-violet-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-violet-600" />;
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      // Build query params for date filters
      const params = new URLSearchParams();
      
      // Add ledger date filter
      if (ledgerDateRange?.from) {
        params.append('ledger_from', format(ledgerDateRange.from, 'yyyy-MM-dd'));
      }
      if (ledgerDateRange?.to) {
        params.append('ledger_to', format(ledgerDateRange.to, 'yyyy-MM-dd'));
      }
      
      // Add costing date filter
      if (costingDateRange?.from) {
        params.append('costing_from', format(costingDateRange.from, 'yyyy-MM-dd'));
      }
      if (costingDateRange?.to) {
        params.append('costing_to', format(costingDateRange.to, 'yyyy-MM-dd'));
      }
      
      // Add sales date filter
      if (salesDateRange?.from) {
        params.append('sales_from', format(salesDateRange.from, 'yyyy-MM-dd'));
      }
      if (salesDateRange?.to) {
        params.append('sales_to', format(salesDateRange.to, 'yyyy-MM-dd'));
      }
      
      // Add errors date filter
      if (errorsDateRange?.from) {
        params.append('errors_from', format(errorsDateRange.from, 'yyyy-MM-dd'));
      }
      if (errorsDateRange?.to) {
        params.append('errors_to', format(errorsDateRange.to, 'yyyy-MM-dd'));
      }
      
      // Add uploads date filter
      if (uploadsDateRange?.from) {
        params.append('uploads_from', format(uploadsDateRange.from, 'yyyy-MM-dd'));
      }
      if (uploadsDateRange?.to) {
        params.append('uploads_to', format(uploadsDateRange.to, 'yyyy-MM-dd'));
      }
      
      const queryString = params.toString();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/dashboard/stats${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        toast.error("Failed to fetch dashboard stats");
      }
    } catch (error) {
      toast.error("Error loading dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [token, ledgerDateRange, costingDateRange, salesDateRange, errorsDateRange, uploadsDateRange]);

  const fetchClientReport = async () => {
    setLoadingReport(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/dashboard/client-report`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setClientReport(data);
        setShowClientReport(true);
      } else {
        toast.error("Failed to fetch client report");
      }
    } catch (error) {
      toast.error("Error loading client report");
    } finally {
      setLoadingReport(false);
    }
  };

  const fetchOrdersList = async (page = 1, sortBy = 'total_cost', sortDir = 'desc') => {
    setLoadingOrders(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/dashboard/orders-list?page=${page}&limit=100&sort_by=${sortBy}&sort_dir=${sortDir}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setOrdersList(data);
        setOrdersPage(page);
        setShowOrdersList(true);
      } else {
        toast.error("Failed to fetch orders list");
      }
    } catch (error) {
      toast.error("Error loading orders list");
    } finally {
      setLoadingOrders(false);
    }
  };

  const exportOrdersList = () => {
    if (!ordersList?.orders) return;
    
    const headers = ["#", "Client", "Tracking", "Product", "Qty", "Med Cost", "Ship Cost", "Total", "Service", "Country", "Status"];
    const rows = ordersList.orders.map(o => [
      o.order_num,
      o.client || "",
      o.tracking || "",
      o.product || "",
      o.quantity || "",
      o.med_cost?.toFixed(2) || "0.00",
      o.ship_cost?.toFixed(2) || "0.00",
      o.total?.toFixed(2) || "0.00",
      o.service || "",
      o.country || "",
      o.has_errors ? "Error" : "OK"
    ]);
    
    const csvContent = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders_list_page_${ordersPage}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Orders exported successfully!");
  };

  const exportClientReport = () => {
    if (!clientReport?.clients) return;
    
    const headers = ["Client", "Med Cost", "Ship Cost", "Total", "Parcels", "Items"];
    const rows = clientReport.clients.map(c => [
      c.client || "Unknown",
      c.med_cost?.toFixed(2) || "0.00",
      c.ship_cost?.toFixed(2) || "0.00",
      c.total?.toFixed(2) || "0.00",
      c.parcels || 0,
      c.items || 0
    ]);
    
    // Add grand total row
    rows.push([
      "GRAND TOTAL",
      clientReport.grand_total?.med_cost?.toFixed(2) || "0.00",
      clientReport.grand_total?.ship_cost?.toFixed(2) || "0.00",
      clientReport.grand_total?.total?.toFixed(2) || "0.00",
      clientReport.grand_total?.parcels || 0,
      clientReport.grand_total?.items || 0
    ]);
    
    const csvContent = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `client_report_${clientReport.month}_${clientReport.year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported successfully!");
  };

  // Fetch orders for a specific client
  const fetchClientOrders = async (clientName) => {
    setLoadingClientOrders(true);
    setSelectedClient(clientName);
    setClientDetailPage(1);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/shipment-costing/orders?limit=10000&client=${encodeURIComponent(clientName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setClientOrders(data.orders || []);
        setShowClientDetail(true);
      } else {
        toast.error("Failed to fetch client orders");
      }
    } catch (error) {
      toast.error("Error loading client orders");
    } finally {
      setLoadingClientOrders(false);
    }
  };

  // Print client data with selected columns
  const printClientData = () => {
    if (!clientOrders.length) return;
    
    const printWindow = window.open('', '_blank');
    const selectedCols = Object.entries(printColumns).filter(([, v]) => v).map(([k]) => k);
    
    const colNames = {
      order_date: 'Date',
      tracking: 'Tracking',
      product: 'Product',
      qty: 'Qty',
      service: 'Service',
      country: 'Country',
      med_cost: 'Med Cost',
      ship_cost: 'Ship Cost',
      total: 'Total'
    };
    
    // Calculate totals
    const totals = {
      med_cost: clientOrders.reduce((sum, o) => sum + (o.medicine_cost || 0), 0),
      ship_cost: clientOrders.reduce((sum, o) => sum + (o.shipping_cost || 0), 0),
      total: clientOrders.reduce((sum, o) => sum + (o.total_cost || 0), 0),
      qty: clientOrders.reduce((sum, o) => sum + (parseInt(o.quantity) || 0), 0)
    };
    
    // Document styles
    const documentStyles = `
      body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
      h1 { font-size: 18px; margin-bottom: 5px; }
      h2 { font-size: 14px; color: #666; margin-bottom: 15px; }
      table { width: 100%; border-collapse: collapse; margin-top: 10px; }
      th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
      th { background-color: #4a5568; color: white; font-size: 11px; text-transform: uppercase; }
      tr:nth-child(even) { background-color: #f9f9f9; }
      .text-right { text-align: right; }
      .total-row { font-weight: bold; background-color: #e2e8f0 !important; }
      .summary { margin-top: 20px; padding: 10px; background: #f0f4ff; border-radius: 5px; }
    `;
    
    // Document content
    const documentContent = `
      <h1>${selectedClient} - Orders Report</h1>
      <h2>Generated: ${formatDMY(new Date())} | Total Orders: ${clientOrders.length}</h2>
      <table>
        <thead>
          <tr>
            <th>#</th>
            ${selectedCols.map(c => `<th class="${['med_cost','ship_cost','total','qty'].includes(c) ? 'text-right' : ''}">${colNames[c]}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${clientOrders.map((o, i) => `
            <tr>
              <td>${i + 1}</td>
              ${selectedCols.map(c => {
                if (c === 'order_date') return `<td>${formatDMY(o.order_date)}</td>`;
                if (c === 'tracking') return `<td>${o.tracking || '-'}</td>`;
                if (c === 'product') return `<td>${o.second_name || o.product || '-'}</td>`;
                if (c === 'qty') return `<td class="text-right">${o.quantity || '-'}</td>`;
                if (c === 'service') return `<td>${o.detected_service || '-'}</td>`;
                if (c === 'country') return `<td>${o.detected_country || '-'}</td>`;
                if (c === 'med_cost') return `<td class="text-right">₹${(o.medicine_cost || 0).toFixed(2)}</td>`;
                if (c === 'ship_cost') return `<td class="text-right">₹${(o.shipping_cost || 0).toFixed(2)}</td>`;
                if (c === 'total') return `<td class="text-right">₹${(o.total_cost || 0).toFixed(2)}</td>`;
                return '<td>-</td>';
              }).join('')}
            </tr>
          `).join('')}
          <tr class="total-row">
            <td colspan="${selectedCols.includes('country') ? selectedCols.indexOf('country') + 2 : selectedCols.indexOf('service') + 2}">TOTAL</td>
            ${selectedCols.includes('med_cost') ? `<td class="text-right">$${totals.med_cost.toFixed(2)}</td>` : ''}
            ${selectedCols.includes('ship_cost') ? `<td class="text-right">$${totals.ship_cost.toFixed(2)}</td>` : ''}
            ${selectedCols.includes('total') ? `<td class="text-right">$${totals.total.toFixed(2)}</td>` : ''}
          </tr>
        </tbody>
      </table>
      <div class="summary">
        <strong>Summary:</strong> ${clientOrders.length} orders | 
        Med Cost: $${totals.med_cost.toLocaleString(undefined, {minimumFractionDigits: 2})} | 
        Ship Cost: $${totals.ship_cost.toLocaleString(undefined, {minimumFractionDigits: 2})} | 
        Total: $${totals.total.toLocaleString(undefined, {minimumFractionDigits: 2})}
      </div>
    `;
    
    // Print preview with toolbar (preview-first approach)
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Preview - ${selectedClient} Orders Report</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #f1f5f9; min-height: 100vh; }
          
          /* Preview Toolbar */
          .preview-toolbar {
            position: sticky;
            top: 0;
            z-index: 100;
            background: linear-gradient(to right, ${printSettings.headerColor}, ${printSettings.headerColor}99);
            padding: 12px 24px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }
          .preview-toolbar h1 {
            color: white;
            font-size: 18px;
            font-weight: 600;
          }
          .preview-toolbar .subtitle {
            color: rgba(255,255,255,0.8);
            font-size: 12px;
            margin-top: 2px;
          }
          .toolbar-buttons {
            display: flex;
            gap: 10px;
          }
          .toolbar-btn {
            padding: 10px 20px;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: all 0.2s;
          }
          .btn-print {
            background: white;
            color: #4a5568;
          }
          .btn-print:hover {
            background: #f7fafc;
            transform: translateY(-1px);
          }
          .btn-pdf {
            background: #059669;
            color: white;
          }
          .btn-pdf:hover {
            background: #047857;
            transform: translateY(-1px);
          }
          .btn-close {
            background: rgba(255,255,255,0.2);
            color: white;
          }
          .btn-close:hover {
            background: rgba(255,255,255,0.3);
          }
          
          /* Preview Container */
          .preview-container {
            padding: 24px;
            display: flex;
            justify-content: center;
          }
          .preview-page {
            background: white;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            border-radius: 8px;
            max-width: 1000px;
            width: 100%;
            overflow: hidden;
          }
          .preview-content {
            padding: 40px;
          }
          
          /* Print tip */
          .print-tip {
            background: #fef3c7;
            border: 1px solid #f59e0b;
            color: #92400e;
            padding: 10px 16px;
            margin: 16px 24px;
            border-radius: 8px;
            font-size: 13px;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          
          /* Document Styles */
          .preview-content { ${documentStyles} }
          
          /* Hide toolbar when printing */
          @media print {
            .preview-toolbar, .print-tip { display: none !important; }
            .preview-container { padding: 0; }
            .preview-page { box-shadow: none; border-radius: 0; }
            body { background: white; margin: 10px; }
          }
        </style>
        <script>
          // Print function that keeps window open after print/cancel
          function handlePrint() {
            window.print();
            // Window stays open - user must manually close it
          }
        </script>
      </head>
      <body>
        <div class="preview-toolbar">
          <div>
            <h1>🖨️ Print Preview</h1>
            <div class="subtitle">${selectedClient} - Orders Report</div>
          </div>
          <div class="toolbar-buttons">
            <button class="toolbar-btn btn-close" onclick="window.close()">
              ✕ Close
            </button>
            <button class="toolbar-btn btn-pdf" onclick="handlePrint()">
              📄 Save as PDF
            </button>
            <button class="toolbar-btn btn-print" onclick="handlePrint()">
              🖨️ Print
            </button>
          </div>
        </div>
        
        <div class="print-tip">
          💡 <strong>Tip:</strong> Review the content below. Click "Print" to send to printer or "Save as PDF" to download. The preview will stay open after printing.
        </div>
        
        <div class="preview-container">
          <div class="preview-page">
            <div class="preview-content">
              ${documentContent}
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    setShowPrintDialog(false);
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color, onClick, trend }) => (
    <Card 
      className={`cursor-pointer transition-all duration-300 hover:shadow-lg active:scale-[0.98] lg:hover:scale-[1.02] hover:border-${color}-500/50 bg-gradient-to-br from-background to-muted/30 touch-manipulation`}
      onClick={onClick}
    >
      <CardContent className="p-3 lg:p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] lg:text-xs font-medium text-muted-foreground uppercase tracking-wide truncate">{title}</p>
            <p className="text-lg lg:text-2xl font-bold mt-0.5 lg:mt-1 truncate">{value}</p>
            {subtitle && (
              <p className="text-[10px] lg:text-xs text-muted-foreground mt-0.5 lg:mt-1 hidden sm:block">{subtitle}</p>
            )}
            {trend !== undefined && (
              <div className="flex items-center gap-1 mt-1 lg:mt-2">
                <TrendingUp className={`w-3 h-3 ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`} />
                <span className={`text-[10px] lg:text-xs ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {trend >= 0 ? '+' : ''}{trend}%
                </span>
              </div>
            )}
          </div>
          <div className={`p-2 lg:p-3 rounded-xl bg-${color}-500/10 flex-shrink-0`}>
            <Icon className={`w-5 h-5 lg:w-6 lg:h-6 text-${color}-500`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const SectionHeader = ({ title, icon: Icon, color, path, count, dateRange, onDateChange }) => (
    <div 
      className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3 lg:mb-4"
    >
      <div 
        className="flex items-center gap-2 lg:gap-3 cursor-pointer group touch-manipulation"
        onClick={() => navigate(path)}
      >
        <div className={`p-1.5 lg:p-2 rounded-lg bg-${color}-500/10 flex-shrink-0`}>
          <Icon className={`w-4 h-4 lg:w-5 lg:h-5 text-${color}-500`} />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base lg:text-lg font-semibold truncate">{title}</h2>
          {count !== undefined && (
            <p className="text-[10px] lg:text-xs text-muted-foreground">{count} records</p>
          )}
        </div>
        <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity hidden lg:flex h-8">
          View All <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
        <ArrowRight className="w-4 h-4 text-muted-foreground lg:hidden" />
      </div>
      {onDateChange && (
        <div className="self-end sm:self-auto">
          <DateRangeFilter 
            dateRange={dateRange} 
            onDateChange={onDateChange}
          />
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-4 lg:space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 lg:h-8 w-32 lg:w-48" />
          <Skeleton className="h-9 lg:h-10 w-10 lg:w-24" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-24 lg:h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Header - Mobile optimized */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl lg:text-2xl font-bold truncate">Dashboard</h1>
          <p className="text-muted-foreground text-xs lg:text-sm hidden sm:block">Overview of your EAC data</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStats} disabled={loading} className="flex-shrink-0 h-9 lg:h-10">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''} lg:mr-2`} />
          <span className="hidden lg:inline">Refresh</span>
        </Button>
      </div>

      {/* ==================== SECTION 1: LEDGER RECONCILIATION ==================== */}
      <section>
        <SectionHeader 
          title="Ledger Reconciliation" 
          icon={Wallet} 
          color="emerald" 
          path="/ledger-reconciliation"
          count={stats?.ledger?.total_partners}
          dateRange={ledgerDateRange}
          onDateChange={setLedgerDateRange}
        />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 lg:gap-4">
          <Card 
            className="col-span-2 lg:col-span-1 cursor-pointer transition-all duration-300 hover:shadow-lg active:scale-[0.98] lg:hover:scale-[1.02] bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 border-emerald-200 dark:border-emerald-800 touch-manipulation"
            onClick={() => openReportInNewTab("/reports/outstanding-balance", ledgerDateRange)}
            data-testid="outstanding-balance-card"
          >
            <CardContent className="p-3 lg:p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] lg:text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">Outstanding Balance</p>
                  <p className="text-lg lg:text-2xl font-bold mt-1 text-emerald-700 dark:text-emerald-300 truncate">
                    {stats?.ledger?.outstanding_balance?.toLocaleString() || 0}
                  </p>
                  <p className="text-[10px] lg:text-xs text-emerald-500 dark:text-emerald-400 mt-1 hidden sm:block">Click for detailed report</p>
                </div>
                <div className="p-2 lg:p-3 rounded-xl bg-emerald-500/10 flex-shrink-0">
                  <CircleDollarSign className="w-5 h-5 lg:w-6 lg:h-6 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <StatCard
            title="Total Partners"
            value={stats?.ledger?.total_partners || 0}
            subtitle="In ledger"
            icon={Users}
            color="blue"
            onClick={() => navigate("/ledger-reconciliation")}
          />
          <StatCard
            title="With Balance"
            value={stats?.ledger?.partners_with_balance || 0}
            subtitle="Partners with dues"
            icon={AlertTriangle}
            color="amber"
            onClick={() => navigate("/ledger-reconciliation")}
          />
          <StatCard
            title="Total Paid"
            value={`₹${(stats?.ledger?.total_paid || 0).toLocaleString()}`}
            subtitle="Collections received"
            icon={CheckCircle}
            color="green"
            onClick={() => navigate("/ledger-reconciliation")}
          />
          <Card 
            className="col-span-2 lg:col-span-1 cursor-pointer transition-all duration-300 hover:shadow-lg active:scale-[0.98] lg:hover:scale-[1.02] bg-gradient-to-br from-background to-muted/30 touch-manipulation"
            onClick={() => openReportInNewTab("/reports/aging-summary", ledgerDateRange)}
            data-testid="aging-summary-card"
          >
            <CardContent className="p-3 lg:p-5">
              <p className="text-[10px] lg:text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 lg:mb-3">Aging Summary</p>
              <div className="space-y-1 lg:space-y-1.5">
                <div className="flex items-center justify-between text-xs lg:text-sm">
                  <span className="text-green-600">0-30 days</span>
                  <Badge variant="outline" className="text-green-600 text-[10px] lg:text-xs">₹{(stats?.ledger?.aging?.["0_30_days"] || 0).toLocaleString()}</Badge>
                </div>
                <div className="flex items-center justify-between text-xs lg:text-sm">
                  <span className="text-amber-600">31-60 days</span>
                  <Badge variant="outline" className="text-amber-600 text-[10px] lg:text-xs">₹{(stats?.ledger?.aging?.["31_60_days"] || 0).toLocaleString()}</Badge>
                </div>
                <div className="flex items-center justify-between text-xs lg:text-sm">
                  <span className="text-orange-600">61-90 days</span>
                  <Badge variant="outline" className="text-orange-600 text-[10px] lg:text-xs">₹{(stats?.ledger?.aging?.["61_90_days"] || 0).toLocaleString()}</Badge>
                </div>
                <div className="flex items-center justify-between text-xs lg:text-sm">
                  <span className="text-red-600">90+ days</span>
                  <Badge variant="outline" className="text-red-600 text-[10px] lg:text-xs">₹{(stats?.ledger?.aging?.["over_90_days"] || 0).toLocaleString()}</Badge>
                </div>
              </div>
              <p className="text-[10px] lg:text-xs text-muted-foreground mt-2 hidden sm:block">Click for detailed report</p>
            </CardContent>
          </Card>
        </div>

        {/* Uncollected Billing tile — bridges the Ledger ↔ Sales & Profit
            gap for finance (shipped-but-not-invoiced backlog per partner). */}
        <UncollectedBillingTile token={token} />
        
        {/* Payment Mode Breakdown */}
        {stats?.ledger?.payment_modes?.length > 0 && (
          <Card className="mt-3 lg:mt-4 cursor-pointer transition-all duration-300 hover:shadow-lg active:scale-[0.995] lg:hover:scale-[1.005] bg-gradient-to-br from-background to-muted/30 touch-manipulation"
            onClick={() => navigate("/ledger-reconciliation")}>
            <CardContent className="p-3 lg:p-5">
              <p className="text-[10px] lg:text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 lg:mb-3">Payment Mode Breakdown</p>
              <div className="grid grid-cols-3 lg:grid-cols-5 gap-2 lg:gap-3">
                {stats.ledger.payment_modes.map((mode, i) => (
                  <div key={i} className="p-2 lg:p-3 rounded-lg bg-muted/50 text-center">
                    <p className="text-sm lg:text-lg font-bold truncate">₹{(mode.total || 0).toLocaleString()}</p>
                    <p className="text-[10px] lg:text-xs text-muted-foreground truncate">{mode._id || "Unknown"}</p>
                    <Badge variant="secondary" className="mt-1 text-[10px] lg:text-xs">{mode.count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Pending Sales Reports Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 lg:gap-4 mt-3 lg:mt-4">
          <Card 
            className="cursor-pointer transition-all duration-300 hover:shadow-lg active:scale-[0.98] lg:hover:scale-[1.02] bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-800 touch-manipulation"
            onClick={() => openReportInNewTab("/reports/pending-sales", ledgerDateRange)}
            data-testid="pending-sales-card"
          >
            <CardContent className="p-3 lg:p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] lg:text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wide">Pending Sales Report</p>
                  <p className="text-xs lg:text-sm text-amber-700 dark:text-amber-300 mt-1 lg:mt-2 line-clamp-2">
                    Partners with shipments but no sales uploads
                  </p>
                  <p className="text-[10px] lg:text-xs text-amber-500 dark:text-amber-400 mt-1 lg:mt-2 hidden sm:block">Click to view & export</p>
                </div>
                <div className="p-2 lg:p-3 rounded-xl bg-amber-500/10 flex-shrink-0">
                  <FileSpreadsheet className="w-5 h-5 lg:w-6 lg:h-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer transition-all duration-300 hover:shadow-lg active:scale-[0.98] lg:hover:scale-[1.02] bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30 border-purple-200 dark:border-purple-800 touch-manipulation"
            onClick={() => openReportInNewTab("/reports/pending-auto-generate", ledgerDateRange)}
            data-testid="pending-auto-generate-card"
          >
            <CardContent className="p-3 lg:p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] lg:text-xs font-medium text-purple-600 dark:text-purple-400 uppercase tracking-wide">Pending Sale Auto Generate</p>
                  <p className="text-xs lg:text-sm text-purple-700 dark:text-purple-300 mt-1 lg:mt-2 line-clamp-2">
                    DP Clients with missing prices or shipping rates
                  </p>
                  <p className="text-[10px] lg:text-xs text-purple-500 dark:text-purple-400 mt-1 lg:mt-2 hidden sm:block">Click to view & export</p>
                </div>
                <div className="p-2 lg:p-3 rounded-xl bg-purple-500/10 flex-shrink-0">
                  <Calculator className="w-5 h-5 lg:w-6 lg:h-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ==================== SECTION 2: SHIPMENT COSTING ==================== */}
      <section>
        <SectionHeader 
          title="Shipment Costing" 
          icon={Calculator} 
          color="rose" 
          path="/costing"
          count={stats?.costing?.total_orders}
          dateRange={costingDateRange}
          onDateChange={setCostingDateRange}
        />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 lg:gap-4">
          <Card 
            className={`col-span-2 lg:col-span-1 cursor-pointer transition-all duration-300 hover:shadow-lg active:scale-[0.98] lg:hover:scale-[1.02] hover:border-rose-500/50 bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30 border-rose-200 dark:border-rose-800 touch-manipulation ${loadingOrders ? 'opacity-70' : ''}`}
            onClick={() => fetchOrdersList(1, 'total_cost', 'desc')}
          >
            <CardContent className="p-3 lg:p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] lg:text-xs font-medium text-rose-600 dark:text-rose-400 uppercase tracking-wide">Total Orders</p>
                  <p className="text-lg lg:text-2xl font-bold mt-1 text-rose-700 dark:text-rose-300">
                    {stats?.costing?.total_orders || 0}
                  </p>
                  <p className="text-[10px] lg:text-xs text-rose-500 dark:text-rose-400 mt-1 hidden sm:block">Click to view all orders</p>
                </div>
                <div className="p-2 lg:p-3 rounded-xl bg-rose-500/10 flex-shrink-0">
                  <Calculator className="w-5 h-5 lg:w-6 lg:h-6 text-rose-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <StatCard
            title="Successful"
            value={stats?.costing?.successful_orders || 0}
            subtitle="Orders without errors"
            icon={CheckCircle}
            color="green"
            onClick={() => navigate("/costing?filter=success")}
          />
          <StatCard
            title="With Errors"
            value={stats?.costing?.orders_with_errors || 0}
            subtitle="Need attention"
            icon={AlertTriangle}
            color="amber"
            onClick={() => navigate("/costing?filter=errors")}
          />
          <StatCard
            title="Total Cost"
            value={`₹${(stats?.costing?.total_cost || 0).toLocaleString()}`}
            subtitle="Combined shipping cost"
            icon={DollarSign}
            color="sky"
            onClick={() => navigate("/costing")}
          />
          <Card 
            className={`col-span-2 lg:col-span-1 cursor-pointer transition-all duration-300 hover:shadow-lg active:scale-[0.98] lg:hover:scale-[1.02] hover:border-violet-500/50 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 border-violet-200 dark:border-violet-800 touch-manipulation ${loadingReport ? 'opacity-70' : ''}`}
            onClick={fetchClientReport}
          >
            <CardContent className="p-3 lg:p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] lg:text-xs font-medium text-violet-600 dark:text-violet-400 uppercase tracking-wide">Client Report</p>
                  <p className="text-lg lg:text-2xl font-bold mt-1 text-violet-700 dark:text-violet-300">
                    {stats?.costing?.unique_clients || 0}
                  </p>
                  <p className="text-[10px] lg:text-xs text-violet-500 dark:text-violet-400 mt-1 hidden sm:block">Click for monthly summary</p>
                </div>
                <div className="p-2 lg:p-3 rounded-xl bg-violet-500/10 flex-shrink-0">
                  <FileText className="w-5 h-5 lg:w-6 lg:h-6 text-violet-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Additional Costing Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 lg:gap-4 mt-3 lg:mt-4">
          <Card 
            className="cursor-pointer transition-all duration-300 hover:shadow-lg active:scale-[0.98] lg:hover:scale-[1.01] bg-gradient-to-br from-background to-muted/30 touch-manipulation"
            onClick={() => navigate("/costing")}
          >
            <CardContent className="p-3 lg:p-5">
              <p className="text-[10px] lg:text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 lg:mb-3">Orders by Service</p>
              <div className="space-y-1.5 lg:space-y-2">
                {stats?.costing?.by_service?.map((svc, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-xs lg:text-sm truncate">{svc._id || "Unknown"}</span>
                    <Badge variant="outline" className="text-[10px] lg:text-xs ml-2 flex-shrink-0">{svc.count} orders</Badge>
                  </div>
                ))}
                {(!stats?.costing?.by_service || stats.costing.by_service.length === 0) && (
                  <p className="text-xs lg:text-sm text-muted-foreground">No orders yet</p>
                )}
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer transition-all duration-300 hover:shadow-lg active:scale-[0.98] lg:hover:scale-[1.01] bg-gradient-to-br from-background to-muted/30 touch-manipulation"
            onClick={() => navigate("/costing")}
          >
            <CardContent className="p-3 lg:p-5">
              <p className="text-[10px] lg:text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 lg:mb-3">Top Countries</p>
              <div className="space-y-1.5 lg:space-y-2">
                {stats?.costing?.top_countries?.map((country, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <Globe className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                      <span className="text-xs lg:text-sm truncate">{country._id || "Unknown"}</span>
                    </div>
                    <Badge variant="secondary" className="text-[10px] lg:text-xs ml-2 flex-shrink-0">{country.count}</Badge>
                  </div>
                ))}
                {(!stats?.costing?.top_countries || stats.costing.top_countries.length === 0) && (
                  <p className="text-xs lg:text-sm text-muted-foreground">No country data yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ==================== SECTION 3: SALES & PROFIT ==================== */}
      <section>
        <SectionHeader 
          title="Sales & Profit" 
          icon={TrendingUp} 
          color="indigo" 
          path="/sales-profit"
          count={stats?.sales?.parcel_count}
          dateRange={salesDateRange}
          onDateChange={setSalesDateRange}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card 
            className="cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-950/30 dark:to-blue-950/30 border-indigo-200 dark:border-indigo-800"
            onClick={() => openReportInNewTab("/reports/sales-profit", salesDateRange)}
            data-testid="total-sales-card"
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wide">Total Sales</p>
                  <p className="text-2xl font-bold mt-1 text-indigo-700 dark:text-indigo-300">
                    ₹{(stats?.sales?.total_sales || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-1">Click for detailed report</p>
                </div>
                <div className="p-3 rounded-xl bg-indigo-500/10">
                  <BarChart3 className="w-6 h-6 text-indigo-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <StatCard
            title="Total GP"
            value={`₹${(stats?.sales?.total_gp || 0).toLocaleString()}`}
            subtitle="Gross profit"
            icon={TrendingUp}
            color="green"
            onClick={() => openReportInNewTab("/reports/sales-profit", salesDateRange)}
          />
          <StatCard
            title="Self Share"
            value={`₹${(stats?.sales?.self_share || 0).toLocaleString()}`}
            subtitle="Company profit"
            icon={Building2}
            color="blue"
            onClick={() => openReportInNewTab("/reports/sales-profit", salesDateRange)}
          />
          <StatCard
            title="Partner Share"
            value={`₹${(stats?.sales?.partner_share || 0).toLocaleString()}`}
            subtitle="Partner profit"
            icon={Users}
            color="purple"
            onClick={() => openReportInNewTab("/reports/sales-profit", salesDateRange)}
          />
          <Card 
            className="cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] bg-gradient-to-br from-background to-muted/30"
            onClick={() => openReportInNewTab("/reports/sales-profit", salesDateRange)}
            data-testid="top-partners-sales-card"
          >
            <CardContent className="p-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Top Partners by Sales</p>
              <div className="space-y-2">
                {stats?.sales?.top_partners?.slice(0, 3).map((partner, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm truncate">{partner._id || "Unknown"}</span>
                    <Badge variant="secondary" className="text-xs">₹{(partner.total_sales || 0).toLocaleString()}</Badge>
                  </div>
                ))}
                {(!stats?.sales?.top_partners || stats.sales.top_partners.length === 0) && (
                  <p className="text-sm text-muted-foreground">No sales data yet</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Click for detailed report</p>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200"
            onClick={() => openReportInNewTab("/reports/sales-comparison", salesDateRange)}
            data-testid="sales-comparison-card"
          >
            <CardContent className="p-5">
              <p className="text-xs font-medium text-violet-600 uppercase tracking-wide">Sales Comparison</p>
              <p className="text-lg font-bold mt-1 text-violet-700">Partner / Client / Agent</p>
              <p className="text-xs text-violet-500 mt-1">Performance analysis with trends</p>
            </CardContent>
          </Card>
        </div>
      </section>
      <section>
        <SectionHeader 
          title="Error Reconciliation" 
          icon={AlertTriangle} 
          color="amber" 
          path="/errors"
          count={stats?.errors?.total}
          dateRange={errorsDateRange}
          onDateChange={setErrorsDateRange}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card 
            className="cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/30 border-amber-200 dark:border-amber-800"
            onClick={() => openReportInNewTab("/reports/error-reconciliation", errorsDateRange)}
            data-testid="total-errors-card"
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wide">Total Errors</p>
                  <p className="text-2xl font-bold mt-1 text-amber-700 dark:text-amber-300">
                    {stats?.errors?.total || 0}
                  </p>
                  <p className="text-xs text-amber-500 dark:text-amber-400 mt-1">Click for detailed report</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-500/10">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <StatCard
            title="Pending"
            value={stats?.errors?.pending || 0}
            subtitle="Awaiting resolution"
            icon={Clock}
            color="orange"
            onClick={() => navigate("/errors?filter=pending")}
          />
          <StatCard
            title="Resolved"
            value={stats?.errors?.resolved || 0}
            subtitle="Fixed errors"
            icon={CheckCircle}
            color="green"
            onClick={() => navigate("/errors?filter=resolved")}
          />
          <Card 
            className="cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] bg-gradient-to-br from-background to-muted/30"
            onClick={() => openReportInNewTab("/reports/error-reconciliation", errorsDateRange)}
            data-testid="error-types-card"
          >
            <CardContent className="p-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Error Types</p>
              <div className="space-y-2">
                {stats?.errors?.by_type?.slice(0, 3).map((error, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm truncate">{error._id || "Unknown"}</span>
                    <Badge variant="destructive" className="text-xs">{error.count}</Badge>
                  </div>
                ))}
                {(!stats?.errors?.by_type || stats.errors.by_type.length === 0) && (
                  <p className="text-sm text-muted-foreground">No errors recorded</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Click for detailed report</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ==================== SECTION 5: UPLOAD CENTRE ==================== */}
      <section>
        <SectionHeader 
          title="Upload Centre" 
          icon={Upload} 
          color="cyan" 
          path="/upload-center"
          count={stats?.uploads?.partner_uploads_total}
          dateRange={uploadsDateRange}
          onDateChange={setUploadsDateRange}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card 
            className="cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] bg-gradient-to-br from-cyan-50 to-sky-50 dark:from-cyan-950/30 dark:to-sky-950/30 border-cyan-200 dark:border-cyan-800"
            onClick={() => navigate("/upload-center")}
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs font-medium text-cyan-600 dark:text-cyan-400 uppercase tracking-wide">Partner Uploads</p>
                  <p className="text-2xl font-bold mt-1 text-cyan-700 dark:text-cyan-300">
                    {stats?.uploads?.partner_uploads_total || 0}
                  </p>
                  <p className="text-xs text-cyan-500 dark:text-cyan-400 mt-1">Total uploaded files</p>
                </div>
                <div className="p-3 rounded-xl bg-cyan-500/10">
                  <FileSpreadsheet className="w-6 h-6 text-cyan-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <StatCard
            title="Pending Review"
            value={stats?.uploads?.partner_uploads_pending || 0}
            subtitle="Need processing"
            icon={Clock}
            color="amber"
            onClick={() => navigate("/upload-center")}
          />
          <StatCard
            title="Successful"
            value={stats?.uploads?.success_count || 0}
            subtitle="Completed uploads"
            icon={CheckCircle}
            color="green"
            onClick={() => navigate("/upload-center")}
          />
          <StatCard
            title="Failed"
            value={stats?.uploads?.failed_count || 0}
            subtitle="Upload errors"
            icon={X}
            color="red"
            onClick={() => navigate("/upload-center")}
          />
        </div>
        
        {/* Recent Uploads */}
        {stats?.uploads?.recent?.length > 0 && (
          <Card className="mt-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.005] bg-gradient-to-br from-background to-muted/30"
            onClick={() => navigate("/upload-center")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Recent Uploads</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats.uploads.recent.slice(0, 5).map((upload, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium truncate max-w-[200px]" title={upload.filename || "Unknown File"}>
                          {upload.filename || "Unknown File"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          by {upload.username || "Unknown"} • {upload.timestamp ? formatDMY(upload.timestamp) : ""}
                        </p>
                      </div>
                    </div>
                    <Badge 
                      variant={upload.status === 'completed' || upload.status === 'approved' ? 'default' : 'secondary'} 
                      className={`text-xs ${upload.status === 'completed' || upload.status === 'approved' ? 'bg-green-100 text-green-700' : upload.status === 'pending' ? 'bg-amber-100 text-amber-700' : ''}`}
                    >
                      {upload.status || "completed"}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      {/* ==================== SECTION 6: PRODUCT PRICE LIST (MASTER DATA) ==================== */}
      <section>
        <SectionHeader 
          title="Product Price List" 
          icon={Package} 
          color="orange" 
          path="/products"
          count={stats?.products?.total}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Products"
            value={stats?.products?.total || 0}
            subtitle="In your catalog"
            icon={Package}
            color="orange"
            onClick={() => navigate("/products")}
          />
          <StatCard
            title="Categories"
            value={stats?.products?.categories || 0}
            subtitle="Product categories"
            icon={FolderOpen}
            color="blue"
            onClick={() => navigate("/products")}
          />
          <StatCard
            title="Companies"
            value={stats?.products?.companies || 0}
            subtitle="Unique suppliers"
            icon={Building2}
            color="purple"
            onClick={() => navigate("/products")}
          />
          <Card 
            className="cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] bg-gradient-to-br from-background to-muted/30"
            onClick={() => navigate("/products")}
          >
            <CardContent className="p-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Top Categories</p>
              <div className="space-y-2">
                {stats?.products?.by_category?.slice(0, 3).map((cat, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm truncate">{cat._id || "Uncategorized"}</span>
                    <Badge variant="secondary" className="text-xs">{cat.count}</Badge>
                  </div>
                ))}
                {(!stats?.products?.by_category || stats.products.by_category.length === 0) && (
                  <p className="text-sm text-muted-foreground">No categories yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ==================== SECTION 7: SHIPPING PRICE LIST (MASTER DATA) ==================== */}
      <section>
        <SectionHeader 
          title="Shipping Price List" 
          icon={Truck} 
          color="teal" 
          path="/shipping"
          count={stats?.shipping?.total}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Shipping Rates"
            value={stats?.shipping?.total || 0}
            subtitle="Active rates"
            icon={Truck}
            color="teal"
            onClick={() => navigate("/shipping")}
          />
          <StatCard
            title="Services"
            value={stats?.shipping?.services || 0}
            subtitle="Shipping services"
            icon={TrendingUp}
            color="cyan"
            onClick={() => navigate("/shipping")}
          />
          <StatCard
            title="Countries"
            value={stats?.shipping?.countries || 0}
            subtitle="Destinations covered"
            icon={Globe}
            color="indigo"
            onClick={() => navigate("/shipping")}
          />
          <Card 
            className="cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] bg-gradient-to-br from-background to-muted/30"
            onClick={() => navigate("/shipping")}
          >
            <CardContent className="p-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">By Service</p>
              <div className="space-y-2">
                {stats?.shipping?.by_service?.slice(0, 3).map((svc, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm truncate">{svc._id || "Unknown"}</span>
                    <Badge variant="secondary" className="text-xs">{svc.count}</Badge>
                  </div>
                ))}
                {(!stats?.shipping?.by_service || stats.shipping.by_service.length === 0) && (
                  <p className="text-sm text-muted-foreground">No services yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ==================== SECTION 8: DATA INTEGRITY ==================== */}
      <DataIntegrityWidget token={token} />

      {/* Orders List Modal - Full Screen */}
      <Dialog open={showOrdersList} onOpenChange={setShowOrdersList}>
        <DialogContent className="max-w-[95vw] w-full h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0 border-b pb-4">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl font-bold flex items-center gap-3">
                <Calculator className="w-6 h-6 text-rose-500" />
                All Orders
                {ordersList && (
                  <Badge variant="secondary" className="ml-2 text-base px-3 py-1">
                    {ordersList.total_count?.toLocaleString()} total
                  </Badge>
                )}
              </DialogTitle>
              <div className="flex items-center gap-3 mr-8">
                <span className="text-sm text-muted-foreground">
                  Page {ordersPage} of {ordersList?.total_pages || 1}
                </span>
                <Button variant="outline" size="sm" onClick={exportOrdersList}>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto mt-4">
            {loadingOrders ? (
              <div className="space-y-2">
                {[...Array(10)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : ordersList?.orders?.length > 0 ? (
              <div className="relative">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow className="bg-rose-50 dark:bg-rose-950/30 hover:bg-rose-50 dark:hover:bg-rose-950/30">
                      <TableHead className="font-semibold text-rose-700 dark:text-rose-300 w-16">#</TableHead>
                      <TableHead 
                        className="font-semibold text-rose-700 dark:text-rose-300 cursor-pointer hover:bg-rose-100 dark:hover:bg-rose-900/50 select-none"
                        onClick={() => handleOrdersSort('client')}
                      >
                        <div className="flex items-center">
                          Client
                          <SortIcon columnKey="client" config={ordersSortConfig} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-rose-700 dark:text-rose-300 cursor-pointer hover:bg-rose-100 dark:hover:bg-rose-900/50 select-none"
                        onClick={() => handleOrdersSort('tracking')}
                      >
                        <div className="flex items-center">
                          Tracking
                          <SortIcon columnKey="tracking" config={ordersSortConfig} />
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold text-rose-700 dark:text-rose-300">Product</TableHead>
                      <TableHead 
                        className="font-semibold text-rose-700 dark:text-rose-300 text-right cursor-pointer hover:bg-rose-100 dark:hover:bg-rose-900/50 select-none"
                        onClick={() => handleOrdersSort('med_cost')}
                      >
                        <div className="flex items-center justify-end">
                          Med Cost
                          <SortIcon columnKey="med_cost" config={ordersSortConfig} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-rose-700 dark:text-rose-300 text-right cursor-pointer hover:bg-rose-100 dark:hover:bg-rose-900/50 select-none"
                        onClick={() => handleOrdersSort('ship_cost')}
                      >
                        <div className="flex items-center justify-end">
                          Ship Cost
                          <SortIcon columnKey="ship_cost" config={ordersSortConfig} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-rose-700 dark:text-rose-300 text-right cursor-pointer hover:bg-rose-100 dark:hover:bg-rose-900/50 select-none"
                        onClick={() => handleOrdersSort('total_cost')}
                      >
                        <div className="flex items-center justify-end">
                          Total
                          <SortIcon columnKey="total_cost" config={ordersSortConfig} />
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold text-rose-700 dark:text-rose-300">Service</TableHead>
                      <TableHead className="font-semibold text-rose-700 dark:text-rose-300">Country</TableHead>
                      <TableHead className="font-semibold text-rose-700 dark:text-rose-300 text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ordersList.orders.map((order, idx) => (
                      <TableRow 
                        key={idx} 
                        className={`hover:bg-rose-50/50 dark:hover:bg-rose-950/20 cursor-pointer ${order.has_errors ? 'bg-red-50/50 dark:bg-red-950/20' : ''}`}
                        onClick={() => {
                          setShowOrdersList(false);
                          navigate(`/costing?tracking=${encodeURIComponent(order.tracking || '')}`);
                        }}
                      >
                        <TableCell className="text-muted-foreground text-sm">{order.order_num}</TableCell>
                        <TableCell className="font-medium">{order.client || "-"}</TableCell>
                        <TableCell className="font-mono text-sm">{order.tracking || "-"}</TableCell>
                        <TableCell className="max-w-[200px] truncate" title={order.product}>{order.product || "-"}</TableCell>
                        <TableCell className="text-right text-green-600 dark:text-green-400">
                          ${(order.med_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right text-blue-600 dark:text-blue-400">
                          ${(order.ship_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          ${(order.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{order.service || "-"}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{order.country || "-"}</TableCell>
                        <TableCell className="text-center">
                          {order.has_errors ? (
                            <Badge variant="destructive" className="text-xs">Error</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">OK</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No orders available</p>
              </div>
            )}
          </div>
          
          {/* Pagination and Grand Total Footer */}
          {ordersList?.orders?.length > 0 && (
            <div className="flex-shrink-0 mt-4 space-y-3">
              {/* Pagination */}
              <div className="flex items-center justify-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={ordersPage <= 1 || loadingOrders}
                  onClick={() => fetchOrdersList(ordersPage - 1, ordersSortConfig.key, ordersSortConfig.direction)}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, ordersList?.total_pages || 1) }, (_, i) => {
                    const pageNum = ordersPage <= 3 ? i + 1 : ordersPage - 2 + i;
                    if (pageNum > (ordersList?.total_pages || 1)) return null;
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === ordersPage ? "default" : "outline"}
                        size="sm"
                        className="w-8 h-8 p-0"
                        onClick={() => fetchOrdersList(pageNum, ordersSortConfig.key, ordersSortConfig.direction)}
                        disabled={loadingOrders}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  {ordersList?.total_pages > 5 && ordersPage < ordersList.total_pages - 2 && (
                    <>
                      <span className="px-1">...</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-8 h-8 p-0"
                        onClick={() => fetchOrdersList(ordersList.total_pages, ordersSortConfig.key, ordersSortConfig.direction)}
                        disabled={loadingOrders}
                      >
                        {ordersList.total_pages}
                      </Button>
                    </>
                  )}
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={ordersPage >= (ordersList?.total_pages || 1) || loadingOrders}
                  onClick={() => fetchOrdersList(ordersPage + 1, ordersSortConfig.key, ordersSortConfig.direction)}
                >
                  Next
                </Button>
              </div>
              
              {/* Grand Total */}
              <div className="bg-gradient-to-r from-rose-100 to-pink-100 dark:from-rose-900/50 dark:to-pink-900/50 p-4 rounded-lg border border-rose-200 dark:border-rose-700">
                <div className="flex items-center justify-between">
                  <div className="font-bold text-rose-700 dark:text-rose-300">
                    GRAND TOTAL (All {ordersList?.total_count?.toLocaleString()} orders)
                  </div>
                  <div className="flex items-center gap-8">
                    <div className="text-right">
                      <span className="text-sm text-muted-foreground mr-2">Med Cost:</span>
                      <span className="font-semibold text-green-600 dark:text-green-400">
                        ${(ordersList?.grand_total?.med_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm text-muted-foreground mr-2">Ship Cost:</span>
                      <span className="font-semibold text-blue-600 dark:text-blue-400">
                        ${(ordersList?.grand_total?.ship_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm text-muted-foreground mr-2">Total:</span>
                      <span className="font-bold text-xl">
                        ${(ordersList?.grand_total?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Client Report Modal - Full Screen */}
      <Dialog open={showClientReport} onOpenChange={setShowClientReport}>
        <DialogContent className="max-w-[95vw] w-full h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0 border-b pb-4">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl font-bold flex items-center gap-3">
                <FileText className="w-6 h-6 text-violet-500" />
                Client-wise Monthly Summary
                {clientReport && (
                  <Badge variant="secondary" className="ml-2 text-base px-3 py-1">
                    {new Date(clientReport.year, clientReport.month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                  </Badge>
                )}
              </DialogTitle>
              <div className="flex items-center gap-3 mr-8">
                <span className="text-sm text-muted-foreground">
                  {clientReport?.client_count || 0} clients
                </span>
                <Button variant="outline" size="sm" onClick={exportClientReport}>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto mt-4">
            {loadingReport ? (
              <div className="space-y-2">
                {[...Array(10)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : sortedClients.length > 0 ? (
              <div className="relative">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow className="bg-violet-50 dark:bg-violet-950/30 hover:bg-violet-50 dark:hover:bg-violet-950/30">
                      <TableHead 
                        className="font-semibold text-violet-700 dark:text-violet-300 cursor-pointer hover:bg-violet-100 dark:hover:bg-violet-900/50 select-none"
                        onClick={() => handleSort('client')}
                      >
                        <div className="flex items-center">
                          Client
                          <SortIcon columnKey="client" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-violet-700 dark:text-violet-300 text-right cursor-pointer hover:bg-violet-100 dark:hover:bg-violet-900/50 select-none"
                        onClick={() => handleSort('med_cost')}
                      >
                        <div className="flex items-center justify-end">
                          Med Cost
                          <SortIcon columnKey="med_cost" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-violet-700 dark:text-violet-300 text-right cursor-pointer hover:bg-violet-100 dark:hover:bg-violet-900/50 select-none"
                        onClick={() => handleSort('ship_cost')}
                      >
                        <div className="flex items-center justify-end">
                          Ship Cost
                          <SortIcon columnKey="ship_cost" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-violet-700 dark:text-violet-300 text-right cursor-pointer hover:bg-violet-100 dark:hover:bg-violet-900/50 select-none"
                        onClick={() => handleSort('total')}
                      >
                        <div className="flex items-center justify-end">
                          Total
                          <SortIcon columnKey="total" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-violet-700 dark:text-violet-300 text-right cursor-pointer hover:bg-violet-100 dark:hover:bg-violet-900/50 select-none"
                        onClick={() => handleSort('parcels')}
                      >
                        <div className="flex items-center justify-end">
                          Parcels
                          <SortIcon columnKey="parcels" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-violet-700 dark:text-violet-300 text-right cursor-pointer hover:bg-violet-100 dark:hover:bg-violet-900/50 select-none"
                        onClick={() => handleSort('items')}
                      >
                        <div className="flex items-center justify-end">
                          Items
                          <SortIcon columnKey="items" />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedClients.map((client, idx) => (
                      <TableRow 
                        key={idx} 
                        className="hover:bg-violet-50/50 dark:hover:bg-violet-950/20 cursor-pointer"
                        onClick={() => {
                          // Pass "Unknown" for empty client codes to properly filter
                          fetchClientOrders(client.client || 'Unknown');
                        }}
                      >
                        <TableCell className="font-medium text-base">{client.client || "Unknown"}</TableCell>
                        <TableCell className="text-right text-green-600 dark:text-green-400 text-base">
                          ${(client.med_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right text-blue-600 dark:text-blue-400 text-base">
                          ${(client.ship_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-base">
                          ${(client.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline" className="text-sm">{client.parcels || 0}</Badge>
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground text-base">{client.items || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No client data available for this period</p>
              </div>
            )}
          </div>
          
          {/* Grand Total Footer - Fixed at bottom */}
          {sortedClients.length > 0 && clientReport?.grand_total && (
            <div className="flex-shrink-0 bg-gradient-to-r from-violet-100 to-purple-100 dark:from-violet-900/50 dark:to-purple-900/50 p-4 mt-4 rounded-lg border border-violet-200 dark:border-violet-700">
              <div className="grid grid-cols-6 gap-4 text-base">
                <div className="font-bold text-violet-700 dark:text-violet-300">
                  GRAND TOTAL
                </div>
                <div className="text-right font-semibold text-green-600 dark:text-green-400">
                  ${(clientReport.grand_total?.med_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-right font-semibold text-blue-600 dark:text-blue-400">
                  ${(clientReport.grand_total?.ship_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-right font-bold text-xl">
                  ${(clientReport.grand_total?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-right font-semibold">
                  {(clientReport.grand_total?.parcels || 0).toLocaleString()} parcels
                </div>
                <div className="text-right text-muted-foreground">
                  {(clientReport.grand_total?.items || 0).toLocaleString()} items
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Client Detail Modal */}
      <Dialog open={showClientDetail} onOpenChange={setShowClientDetail}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 flex flex-col">
          <DialogHeader className="flex-shrink-0 p-6 pb-4 border-b bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/50 dark:to-teal-950/50">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                  {selectedClient} - Orders Detail
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {clientOrders.length} orders | 
                  Total: ${clientOrders.reduce((s, o) => s + (o.total_cost || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowPrintDialog(true)}>
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button variant="outline" size="sm" onClick={() => {
                  setShowClientDetail(false);
                  navigate(`/costing?client=${encodeURIComponent(selectedClient || '')}`);
                }}>
                  <Eye className="w-4 h-4 mr-2" />
                  View in Costing
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto p-6">
            {loadingClientOrders ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : clientOrders.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-emerald-100 dark:bg-emerald-900/50">
                      <TableHead className="font-semibold w-12">#</TableHead>
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Tracking</TableHead>
                      <TableHead className="font-semibold">Product</TableHead>
                      <TableHead className="font-semibold text-right">Qty</TableHead>
                      <TableHead className="font-semibold">Service</TableHead>
                      <TableHead className="font-semibold">Country</TableHead>
                      <TableHead className="font-semibold text-right">Med Cost</TableHead>
                      <TableHead className="font-semibold text-right">Ship Cost</TableHead>
                      <TableHead className="font-semibold text-right">Total</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientOrders.slice((clientDetailPage - 1) * 100, clientDetailPage * 100).map((order, idx) => (
                      <TableRow key={idx} className="hover:bg-emerald-50/50">
                        <TableCell className="text-muted-foreground">{(clientDetailPage - 1) * 100 + idx + 1}</TableCell>
                        <TableCell className="font-mono text-sm">{formatDMY(order.order_date)}</TableCell>
                        <TableCell className="font-mono text-sm">{order.tracking || '-'}</TableCell>
                        <TableCell className="max-w-[200px] truncate" title={order.second_name || order.product}>
                          {order.second_name || order.product || '-'}
                        </TableCell>
                        <TableCell className="text-right">{order.quantity || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{order.detected_service || '-'}</Badge>
                        </TableCell>
                        <TableCell>{order.detected_country || '-'}</TableCell>
                        <TableCell className="text-right text-green-600">${(order.medicine_cost || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right text-blue-600">${(order.shipping_cost || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right font-semibold">${(order.total_cost || 0).toFixed(2)}</TableCell>
                        <TableCell>
                          {order.errors?.length > 0 ? (
                            <Badge variant="destructive" className="text-xs">Error</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs text-green-600 border-green-300">OK</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No orders found for this client</p>
              </div>
            )}
          </div>
          
          {/* Pagination & Summary Footer */}
          {clientOrders.length > 100 && (
            <div className="flex-shrink-0 p-4 border-t bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {((clientDetailPage - 1) * 100) + 1} - {Math.min(clientDetailPage * 100, clientOrders.length)} of {clientOrders.length}
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={clientDetailPage <= 1}
                  onClick={() => setClientDetailPage(p => p - 1)}
                >
                  Previous
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={clientDetailPage * 100 >= clientOrders.length}
                  onClick={() => setClientDetailPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
          
          {/* Grand Total */}
          {clientOrders.length > 0 && (
            <div className="flex-shrink-0 bg-gradient-to-r from-emerald-100 to-teal-100 dark:from-emerald-900/50 dark:to-teal-900/50 p-4 border-t">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-xs text-muted-foreground uppercase">Total Orders</div>
                  <div className="text-xl font-bold">{clientOrders.length}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase">Med Cost</div>
                  <div className="text-xl font-bold text-green-600">
                    ${clientOrders.reduce((s, o) => s + (o.medicine_cost || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase">Ship Cost</div>
                  <div className="text-xl font-bold text-blue-600">
                    ${clientOrders.reduce((s, o) => s + (o.shipping_cost || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase">Grand Total</div>
                  <div className="text-xl font-bold text-emerald-700">
                    ${clientOrders.reduce((s, o) => s + (o.total_cost || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Dialog - Column Selection */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Print Options</DialogTitle>
            <p className="text-sm text-muted-foreground">Select columns to include in print</p>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {Object.entries({
                order_date: 'Date',
                tracking: 'Tracking',
                product: 'Product',
                qty: 'Quantity',
                service: 'Service',
                country: 'Country',
                med_cost: 'Med Cost',
                ship_cost: 'Ship Cost',
                total: 'Total'
              }).map(([key, label]) => (
                <div key={key} className="flex items-center space-x-2">
                  <Checkbox
                    id={`print-${key}`}
                    checked={printColumns[key]}
                    onCheckedChange={(checked) => setPrintColumns(prev => ({ ...prev, [key]: checked }))}
                  />
                  <Label htmlFor={`print-${key}`} className="text-sm cursor-pointer">{label}</Label>
                </div>
              ))}
            </div>
            
            <div className="flex gap-2 pt-4 border-t">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setPrintColumns(Object.fromEntries(Object.keys(printColumns).map(k => [k, true])))}
              >
                Select All
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setPrintColumns(Object.fromEntries(Object.keys(printColumns).map(k => [k, false])))}
              >
                Deselect All
              </Button>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setShowPrintDialog(false)}>Cancel</Button>
            <Button onClick={printClientData} className="bg-emerald-600 hover:bg-emerald-700">
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
